import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import CompanyAddress from "../../components/CompanyAddress";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import UploadedAttachmentList from "../../components/UploadedAttachmentList";
import logo from "../../images/logo.png";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const DRIVER_ABSTRACT_TASK_ID = 80;
const POINT_TYPE_OPTIONS = ["Merit Points", "Demerit Points"];
const NO_VIOLATION_OPTIONS = ["Yes", "No"];
const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "application/vnd.ms-outlook", "application/x-msg", "application/CDFV2", "application/x-ole-storage", "application/octet-stream", ""];

const validateFiles = (fileList) =>
  Array.from(fileList || []).filter((file) => allowedTypes.includes(file.type));

const DriverAbstractForm = () => {
  const navigate = useNavigate();
  const { id: paramId, daId } = useParams();
  const isNew = !daId || daId === "new";

  const role = localStorage.getItem("userRole");
  const isAdmin = role === "1";
  const loggedInId = localStorage.getItem("userId");
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const [form, setForm] = useState({
    da_id: null,
    dateIssued: "",
    medicalExpiration: "",
    pointsType: "",
    noViolations: "",
    numberOfPoints: 0,
    violationDescription: "",
    files: [],
    uploadedFiles: [],
  });
  const [isLocked, setIsLocked] = useState(false);
  const [currentStatus, setCurrentStatus] = useState("not_completed");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeletingFileId, setIsDeletingFileId] = useState(null);
  const fileInputRef = useRef(null);

  const fetchTaskStatus = useCallback(async () => {
    if (!userId) return;

    try {
      const res = await axios.get(`${API_BASE}/api/usertasks/status/${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const referenceStatus =
        daId && !isNew
          ? res.data.status?.[`${DRIVER_ABSTRACT_TASK_ID}_${daId}`] ??
            res.data.status?.[`driver_abstract_${daId}`]
          : null;
      const baseStatus =
        res.data.status?.[String(DRIVER_ABSTRACT_TASK_ID)] ??
        res.data.status?.driver_abstract ??
        "not_completed";
      const status = referenceStatus ?? (!isNew ? baseStatus : "not_completed");

      setCurrentStatus(status);
      setIsLocked(status === "completed" || status === "almost_expired");
    } catch (err) {
      console.error("Failed to fetch driver abstract status", err);
    }
  }, [daId, isNew, userId]);

  useEffect(() => {
    fetchTaskStatus();
  }, [fetchTaskStatus]);

  useEffect(() => {
    if (isNew) return;

    const fetchAbstract = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/driverabstract/detail/${daId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const data = res.data;
        setForm({
          da_id: data.da_id,
          dateIssued: data.da_date_issued ? data.da_date_issued.substring(0, 10) : "",
          medicalExpiration: data.da_medical_expiration
            ? data.da_medical_expiration.substring(0, 10)
            : "",
          pointsType: data.da_points_type || "",
          noViolations: data.da_no_violations || "",
          numberOfPoints: data.da_points ?? 0,
          violationDescription: data.da_violation_description || "",
          files: [],
          uploadedFiles: data.files || [],
        });
      } catch (err) {
        console.error("Failed to fetch driver's abstract detail", err);
        alert("Failed to load Driver's Abstract.");
        navigate(`/driverabstract/${userId}`);
      }
    };

    fetchAbstract();
  }, [daId, isNew, navigate, userId]);

  const handleSelectFiles = (e) => {
    const selected = validateFiles(e.target.files);

    if (selected.length !== (e.target.files?.length || 0)) {
      alert("Incorrect file format.");
    }

    setForm((prev) => ({
      ...prev,
      files: [...prev.files, ...selected],
    }));

    e.target.value = "";
  };

  const removePendingFile = (fileToRemove) => {
    setForm((prev) => ({
      ...prev,
      files: prev.files.filter(
        (file) =>
          !(
            file.name === fileToRemove.name &&
            file.size === fileToRemove.size &&
            file.lastModified === fileToRemove.lastModified
          )
      ),
    }));
  };

  const handleDeleteUploadedFile = async (file) => {
    if (!window.confirm(`Delete uploaded file "${file.original_name}"?`)) return;

    try {
      setIsDeletingFileId(file.id);
      await axios.delete(`${API_BASE}/api/driverabstract/file/${file.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setForm((prev) => ({
        ...prev,
        uploadedFiles: prev.uploadedFiles.filter((f) => f.id !== file.id),
      }));
    } catch (err) {
      console.error("Failed to delete uploaded driver abstract file", err);
      alert("Delete failed");
    } finally {
      setIsDeletingFileId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.dateIssued) {
      alert("Please enter Date Issued.");
      return;
    }

    if (isNew && form.files.length === 0 && form.uploadedFiles.length === 0) {
      alert("Please upload at least one file.");
      return;
    }

    setIsSaving(true);

    try {
      const formData = new FormData();
      form.files.forEach((file) => formData.append("files", file));
      formData.append("userId", userId);
      formData.append("da_id", form.da_id || "");
      formData.append("date_issued", form.dateIssued);
      formData.append("medical_expiration", form.medicalExpiration);
      formData.append("points_type", form.pointsType);
      formData.append("no_violations", form.noViolations);
      formData.append("number_of_points", form.numberOfPoints || 0);
      formData.append("violation_description", form.violationDescription);

      const res = await axios.post(
        `${API_BASE}/api/driverabstract/upload?userId=${userId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      alert("Driver's Abstract saved successfully!");
      const savedDaId = res.data?.da_id || form.da_id;

      if (isNew && savedDaId) {
        navigate(`/driverabstract/${userId}/detail/${savedDaId}`);
      } else {
        navigate(`/driverabstract/${userId}`);
      }
    } catch (err) {
      console.error("Driver abstract save failed", err);
      alert("Save failed");
    } finally {
      setIsSaving(false);
    }
  };

  const isDisabled = isLocked;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span style={{ fontSize: "18px", fontWeight: "500" }}>
          Driver&apos;s Abstract
        </span>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => navigate(`/driverabstract/${userId}`)}
          >
            View List
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate(-1)}
          >
            Return
          </button>
        </div>
      </div>

      <div className="form-wrapper my-4 print-area">
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img src={logo} alt="Company Logo" style={{ width: "150px" }} />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Driver&apos;s Abstract</h2>
          </div>
          <CompanyAddress />
        </div>

        <form onSubmit={handleSubmit}>
          <div className="row mb-3">
            <div className="col-md-3">
              <label className="form-label">Date Issued *</label>
              <input
                type="date"
                className="form-control"
                value={form.dateIssued}
                disabled={isDisabled}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, dateIssued: e.target.value }))
                }
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Medical Expiration</label>
              <input
                type="date"
                className="form-control"
                value={form.medicalExpiration}
                disabled={isDisabled}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    medicalExpiration: e.target.value,
                  }))
                }
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Select Type of Points</label>
              <select
                className="form-select"
                value={form.pointsType}
                disabled={isDisabled}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, pointsType: e.target.value }))
                }
              >
                <option value="">Select Type of Points</option>
                {POINT_TYPE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Number of Points</label>
              <input
                type="number"
                min="0"
                className="form-control"
                value={form.numberOfPoints}
                disabled={isDisabled}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    numberOfPoints: e.target.value,
                  }))
                }
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-3">
              <label className="form-label">Abstract With No Violations</label>
              <select
                className="form-select"
                value={form.noViolations}
                disabled={isDisabled}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, noViolations: e.target.value }))
                }
              >
                <option value="">Select</option>
                {NO_VIOLATION_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-9">
              <label className="form-label">Violation Description</label>
              <textarea
                className="form-control"
                rows={3}
                value={form.violationDescription}
                disabled={isDisabled}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    violationDescription: e.target.value,
                  }))
                }
              />
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label">
              Select File
              {isNew ? " *" : ""}
            </label>
            <input
              type="file"
              className="form-control"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.msg"
              ref={fileInputRef}
              disabled={isDisabled}
              onChange={handleSelectFiles}
            />
          </div>

          {form.files.length > 0 && (
            <div className="mt-4 p-3 bg-light rounded border">
              <div className="text-muted mb-2">Files Ready To Save</div>
              {form.files.map((file, idx) => (
                <div
                  key={`${file.name}_${file.size}_${file.lastModified}_${idx}`}
                  className="d-flex justify-content-between align-items-center mb-2"
                >
                  <span>{file.name}</span>
                  {!isDisabled && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      data-delete-permission-exempt="true"
                      onClick={() => removePendingFile(file)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          <UploadedAttachmentList
            title="Uploaded Files"
            files={form.uploadedFiles}
            getFileKey={(file) => file.id}
            getFileName={(file) => file.original_name}
            getFileViewUrl={(file) =>
              `${API_BASE}/uploads/driverabstracts/${file.path}`
            }
            canDelete={!isDisabled}
            onDeleteFile={handleDeleteUploadedFile}
            deletingFileId={isDeletingFileId}
          />

          <div className="d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary mt-3"
              disabled={isDisabled || isSaving}
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>

        {!isNew && (
          <>
            <hr className="my-4" />
            {currentStatus === "not_completed" && (
              <SignatureManager
                taskName="driver_abstract"
                taskId={DRIVER_ABSTRACT_TASK_ID}
                referenceId={daId}
                userId={userId}
                isAdmin={isAdmin}
              />
            )}
            <ReviewManager
              taskName="driver_abstract"
              taskId={DRIVER_ABSTRACT_TASK_ID}
              referenceId={daId}
              userId={userId}
              isAdmin={isAdmin}
              onReviewed={fetchTaskStatus}
            />
          </>
        )}
      </div>
    </>
  );
};

export default DriverAbstractForm;
